import {Component, EventEmitter, Input, OnDestroy, OnInit, Output, TemplateRef, ViewChild} from '@angular/core';
import {TranslateService} from "@ngx-translate/core";
import {
  CellConversionPathComponent
} from "../../../../shared/components/cell-conversion-path/cell-conversion-path.component";
import {
  AgGridComponentFrameworkComponent
} from "../../../../shared/components/ag-grid/ag-grid-component-framework/ag-grid-component-framework.component";
import {
  AgGridTemplateFrameworkComponent
} from "../../../../shared/components/ag-grid/ag-grid-template-framework/ag-grid-template-framework.component";
import {
  AgGridHeaderConversionPathComponent
} from "../../../../shared/components/ag-grid/ag-grid-header-conversion-path/ag-grid-header-conversion-path.component";
import {AgGridHeaderComponent} from "../../../../shared/components/ag-grid/ag-grid-header/ag-grid-header.component";
import {AgGridEvent} from "@ag-grid-community/core";
import {Observable} from 'rxjs';
import {ReportTransactionIdStore} from '../../../../shared/store/report/report-transaction-id.store';
import {ReportStore} from '../../../../shared/store/report/report.store';
import {AgGridUtils} from '../../../../shared/libraries/ag-grid-utils';
import {AdRequestParams} from '../../../../shared/interfaces/ad-reports';
import {AgGridComponent} from "../../../../shared/components/ag-grid/ag-grid.component";

@Component({
  selector: 'app-transaction-id-report-table',
  templateUrl: './transaction-id-report-table.component.html',
  styleUrls: ['./transaction-id-report-table.component.scss']
})
export class TransactionIdReportTableComponent implements OnInit, OnDestroy {
  @ViewChild(AgGridComponent) public readonly agGridC: AgGridComponent;
  @Input() public suppressTotals: boolean = false;
  @Input() public suppressPageTotals: boolean = false;
  @Input() public requestParams: Partial<AdRequestParams>;
  @Output() public onReady: EventEmitter<AgGridEvent> = new EventEmitter<AgGridEvent>();
  @ViewChild('actions', {read: TemplateRef}) public readonly actionsT: TemplateRef<any>;

  public readonly rowCount$: Observable<number> = this._reportTransactionIdStore.rowCount$;
  public readonly pageSize$: Observable<number> = this._reportTransactionIdStore.pageSize$;
  public readonly transactionIds$: Observable<Array<string>> = this._reportTransactionIdStore.transactionIds$;

  public columnDefs: Array<any> = [
    {
      field: 'id',
      minWidth: 150,
      headerValueGetter: _ => this._translateS.instant('transaction_id.id'),
      valueGetter: row => row.data.transactionId || '-',
      filter: 'agSetColumnFilter',
      filterParams: {
        defaultToNothingSelected: true,
        values: params => this.transactionIds$.subscribe((data) => {
          params.success(data);
        })
      },
      resizable: true,
      sortable: true,
      cellStyle: {'text-align': 'right'},
      headerComponentFramework: AgGridHeaderComponent,
      headerComponentParams: {
        position: 'right'
      }
    },
    {
      field: 'conversion_path',
      minWidth: 500,
      resizable: true,
      headerValueGetter: _ => this._translateS.instant('transaction_id.conversion_path'),
      cellRendererFramework: AgGridComponentFrameworkComponent,
      cellRendererParams: params => {
        return {
          component: CellConversionPathComponent
        };
      },
      headerComponentFramework: AgGridHeaderConversionPathComponent,
      autoHeight: true,
      suppressMenu: true
    },
    {
      field: 'revenue',
      headerValueGetter: _ => this._translateS.instant('transaction_id.revenue'),
      valueGetter: row => row.data.revenu.value,
      valueFormatter: row => row.data.revenu.formatted,
      resizable: true,
      sortable: true,
      filter: 'agNumberColumnFilter',
      cellStyle: {'text-align': 'right'},
      headerComponentFramework: AgGridHeaderComponent,
      headerComponentParams: {
        position: 'right'
      }
    },
    {
      field: 'date',
      headerValueGetter: _ => this._translateS.instant('transaction_id.date'),
      valueGetter: row => row.data.date.formatted,
      resizable: true,
      sortable: true,
      filter: 'agDateColumnFilter',
      filterParams: {
        inRangeFloatingFilterDateFormat: 'YYYY-MM-DD'
      },
      headerComponentFramework: AgGridHeaderComponent,
      headerComponentParams: {
        position: 'left'
      }
    },
    {
      field: 'days',
      headerValueGetter: _ => this._translateS.instant('transaction_id.days'),
      valueGetter: row => row.data.days,
      resizable: true,
      sortable: true,
      filter: 'agNumberColumnFilter',
      cellStyle: {'text-align': 'right'},
      headerComponentFramework: AgGridHeaderComponent,
      headerComponentParams: {
        position: 'right'
      }
    },
    {
      field: 'details',
      headerValueGetter: _ => '',
      cellRendererFramework: AgGridTemplateFrameworkComponent,
      cellRendererParams: params => {
        return {
          ngTemplate: this.actionsT
        };
      },
      resizable: true,
      suppressMenu: true
    }
  ];

  public readonly gridOptions: any = {
    suppressDragLeaveHidesColumns: true,
    groupHeaderHeight: 35,
    headerHeight: 35
  };

  public readonly defaultColDef: any = {
    menuTabs: ['filterMenuTab'],
    filterParams: {
      buttons: ['reset', 'apply']
    }
  };

  constructor(
    private readonly _reportStore: ReportStore,
    private readonly _reportTransactionIdStore: ReportTransactionIdStore,
    private readonly _translateS: TranslateService
  ) {
  }

  ngOnInit(): void {
  }

  ngOnDestroy(): void {
  }

  public onDetails(event: AgGridEvent & { data: any }): void {
    this._reportTransactionIdStore.details(event.data);
  }

  public tableDataChanged(event: AgGridEvent): void {
    if (this.requestParams && (event.type === 'firstDataRendered' || event.type === 'rowDataChanged')) {
      if (this.requestParams.tableColumnState) {
        AgGridUtils.columnStateReset(this.requestParams.tableColumnState);
        event.columnApi.applyColumnState({state: this.requestParams.tableColumnState});
        event.api.setFilterModel(this.requestParams.tableFilterModel);
      }
    }
  }

  public onPaginationPageSizeChanged(limit: number): void {
    this._reportTransactionIdStore.changePaginationPageSize(limit);
  }

  public tableChanged(event: AgGridEvent): void {
    if (event.type === 'sortChanged' || event.type === 'filterChanged') {
      this._reportStore.updateRequestParamsSubmitted({
        tableColumnState: AgGridUtils.columnStateReset(event.columnApi.getColumnState()),
        tableFilterModel: event.api.getFilterModel()
      } as any);
    }
  }

  public gridReady(params: AgGridEvent): void {
    this._reportTransactionIdStore.init(params, this.columnDefs, this.suppressTotals, this.suppressPageTotals);
    this.onReady.emit(params);
  }
}
